import { useCallback, useEffect, useState } from 'react';

import FormRequestBlock from 'components/FormRequestBlock';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import ColorPicker from 'components/ColorPicker';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectUserInfo, selectUserBrandLoading, updateBrand } from 'app/store/user';
import { trackSegmentEvent } from 'app/utils';
import { ACTION, SOURCE } from 'app/analytics';
import { canWrite } from 'app/access';

import './style.scss';
import { toast } from 'sonner';

const validateImageUrl = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};

const BrandBlock = () => {
  const userInfo = useAppSelector(selectUserInfo);
  const { brand } = userInfo || {};
  const canEditBrand = canWrite(userInfo, 'branding');
  const formLoading = useAppSelector(selectUserBrandLoading);

  const [companyName, setCompanyName] = useState(brand?.companyName ?? '');
  const [supportEmail, setSupportEmail] = useState(brand?.supportEmail ?? '');
  const [logoUrl, setLogoUrl] = useState(brand?.logoUrl ?? '');
  const [primaryColor, setPrimaryColor] = useState(brand?.primaryColor ?? '');
  const [isValidLogoUrl, setIsValidLogoUrl] = useState(false);
  const [validatingLogo, setValidatingLogo] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    setCompanyName(brand?.companyName ?? '');
    setSupportEmail(brand?.supportEmail ?? '');
    setLogoUrl(brand?.logoUrl ?? '');
    setPrimaryColor(brand?.primaryColor ?? '');
  }, [brand]);

  const logoUrlChanged = logoUrl !== (brand?.logoUrl ?? '');
  const logoBlocksSubmit = logoUrl !== '' && logoUrlChanged && !isValidLogoUrl;

  const handleLogoUrlChange = useCallback((value: string) => {
    setLogoUrl(value);
    setIsValidLogoUrl(false);
  }, []);

  const handleLogoUrlBlur = useCallback(async () => {
    if (!logoUrl) {
      return;
    }
    try {
      new URL(logoUrl);
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }
    setValidatingLogo(true);
    const isValid = await validateImageUrl(logoUrl);
    setValidatingLogo(false);
    setIsValidLogoUrl(isValid);
    if (!isValid) {
      toast.error('URL does not point to a valid image');
    }
  }, [logoUrl]);

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!canEditBrand || logoBlocksSubmit || validatingLogo) {
      return;
    }

    dispatch(
      updateBrand({
        companyName: companyName.trim() || null,
        supportEmail: supportEmail || null,
        logoUrl: logoUrl || null,
        primaryColor: primaryColor || null,
      }),
    );
    trackSegmentEvent(SOURCE.BRAND_MODULE, ACTION.CLICK, 'update_button');
  };

  return (
    <FormRequestBlock
      styles={{ form: { flexDirection: 'column', gap: '10px' } }}
      title="Brand Settings"
      onSubmit={handleSubmit}
      loading={formLoading}
      disabled={!canEditBrand}
      text={<p>Configure your brand details for customer-facing communications</p>}
    >
      <div className="brand-form">
        <div className="brand-body">
          <div>
            <h3 className="form-request-block-title mb-[5px] flex-1">Company Name</h3>
            <Input
              className="brand-input"
              type="text"
              disabled={!canEditBrand}
              value={companyName}
              placeholder="Enter company name"
              onChange={(e) => setCompanyName(e.target.value)}
            />
          </div>
          <div>
            <h3 className="form-request-block-title mb-[5px] flex-1">Support Email</h3>
            <Input
              className="brand-input"
              type="email"
              disabled={!canEditBrand}
              value={supportEmail}
              placeholder="Enter support email"
              onChange={(e) => setSupportEmail(e.target.value.trim())}
            />
          </div>
          <div>
            <h3 className="form-request-block-title mb-[5px] flex-1">Logo URL</h3>
            <Input
              className="brand-input"
              type="url"
              disabled={!canEditBrand}
              value={logoUrl}
              placeholder="Enter logo URL"
              onChange={(e) => handleLogoUrlChange(e.target.value.trim())}
              onBlur={handleLogoUrlBlur}
            />
          </div>
          <div>
            <h3 className="form-request-block-title mb-[5px] flex-1">Brand Color</h3>
            <ColorPicker value={primaryColor} onChange={setPrimaryColor} disabled={!canEditBrand} />
          </div>
          <div className="flex flex-row justify-end">
            <Button
              type="submit"
              variant="secondary"
              disabled={formLoading || !canEditBrand || logoBlocksSubmit || validatingLogo}
              className="w-[120px]"
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </FormRequestBlock>
  );
};

export default BrandBlock;
